import React, { useEffect, useMemo, useState } from "react";
import { Card, Input, Button, Switch, Tooltip, Tag } from "antd";
import { PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";

export const StemAndLeaf = ({ config = {}, variables = {} }) => {
    // ---- Helpers aligned to Java validator (ints only, 0..9999) ----
    const coerceArray = (v) => {
        if (Array.isArray(v)) return v;
        if (typeof v === "string") {
            // Accept "1,2,3" or "1 2 3" etc.
            return v
                .split(/[\s,]+/)
                .filter(Boolean)
                .map((s) => Number(s));
        }
        return null;
    };

    const sanitizeData = (arr) =>
        (Array.isArray(arr) ? arr : [])
            .map((n) => Number(n))
            .filter((n) => Number.isInteger(n) && n >= 0 && n <= 9999);

    const initialRaw =
        coerceArray(variables.data) ??
        coerceArray(config.data) ??
        [12, 15, 21, 25, 26, 30, 32, 41];

    const [data, setData] = useState(() => sanitizeData(initialRaw));
    const [inputValue, setInputValue] = useState("");
    const [isOrdered, setIsOrdered] = useState(true);

    // Respect backend default split state
    const [isSplit, setIsSplit] = useState(config.splitStems ?? false);

    const [hovered, setHovered] = useState(null); // { stemLabel, stemBase, leaf, value }

    // Optional: keep state in sync if config/variables change at runtime
    useEffect(() => {
        const nextRaw =
            coerceArray(variables.data) ??
            coerceArray(config.data) ??
            [12, 15, 21, 25, 26, 30, 32, 41];
        setData(sanitizeData(nextRaw));
    }, [config.data, variables.data]);

    useEffect(() => {
        setIsSplit(config.splitStems ?? false);
    }, [config.splitStems]);

    // ---- Data processing ----
    const { rows, stemKeys } = useMemo(() => {
        const grouped = {};

        const sorted = isOrdered ? [...data].sort((a, b) => a - b) : data;

        sorted.forEach((num) => {
            const stemBase = Math.floor(num / 10);
            const leaf = num % 10;

            let stemLabel = String(stemBase);

            // Split stems: 0-4 low (.), 5-9 high (*)
            if (isSplit) {
                stemLabel = leaf < 5 ? `${stemBase}.` : `${stemBase}*`;
            }

            if (!grouped[stemLabel]) grouped[stemLabel] = [];
            grouped[stemLabel].push({ val: num, leaf, stemBase });
        });

        const stemSortValue = (k) => {
            // k looks like "12", "12.", "12*"
            const base = Number(k.replace(/[^\d-]/g, ""));
            if (!Number.isFinite(base)) return 0;
            if (!isSplit) return base;
            if (k.endsWith(".")) return base + 0.0;
            if (k.endsWith("*")) return base + 0.5;
            return base;
        };

        const keys = Object.keys(grouped).sort(
            (a, b) => stemSortValue(a) - stemSortValue(b)
        );

        return { rows: grouped, stemKeys: keys };
    }, [data, isOrdered, isSplit]);

    // ---- Interactions ----
    const handleAdd = () => {
        const val = parseInt(inputValue, 10);
        if (Number.isInteger(val) && val >= 0 && val <= 9999) {
            setData((prev) => [...prev, val]);
            setInputValue("");
        }
    };

    const handleRemove = (valToRemove) => {
        setData((prev) => {
            const idx = prev.indexOf(valToRemove);
            if (idx === -1) return prev;
            const next = [...prev];
            next.splice(idx, 1);
            return next;
        });
    };

    // ---- Key rendering ----
    // Prefer dynamic key using hovered selection; otherwise:
    // show backend-provided keyDescription if present, else generate an example.
    const firstStem = stemKeys[0];
    const firstItem = firstStem ? rows[firstStem]?.[0] : null;

    const exampleValue = firstItem?.val ?? 12;
    const exampleStemBase = Math.floor(exampleValue / 10);
    const exampleLeaf = exampleValue % 10;

    const keyStem = hovered ? hovered.stemBase : exampleStemBase;
    const keyLeaf = hovered ? hovered.leaf : exampleLeaf;
    const keyValue = hovered ? hovered.value : exampleValue;

    const showBackendKey = !hovered && typeof config.keyDescription === "string" && config.keyDescription.trim().length > 0;

    return (
        <div className="flexColumn gap-medium width-100">
            <div className="flexRow gap-medium align-start">
                {/* --- MAIN CHART AREA --- */}
                <div className="ch-emph-block bg-white flex1" style={{ minHeight: 300 }}>
                    <div className="ch-stem-leaf-container width-100">
                        <table className="ch-stem-leaf-table" style={{ fontSize: "18px" }}>
                            <thead>
                            <tr>
                                <th className="ch-stem-header text-right padding-r--s border-r--s border-color-subtle">
                                    Stem
                                </th>
                                <th className="ch-leaf-header padding-l--s">Leaf</th>
                            </tr>
                            </thead>
                            <tbody>
                            {stemKeys.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={2}
                                        className="text-center text-slate-500 py-6"
                                    >
                                        No data
                                    </td>
                                </tr>
                            ) : (
                                stemKeys.map((stemLabel) => (
                                    <tr key={stemLabel} className="ch-row-hover">
                                        <td className="ch-stem-col font-mono text-bold text-right padding-r--s border-r--s border-color-dark">
                                            {stemLabel}
                                        </td>
                                        <td className="ch-leaf-col padding-l--s">
                                            <div className="flexWrap gap-xs">
                                                {rows[stemLabel].map((item, i) => (
                                                    <span
                                                        key={`${stemLabel}-${item.val}-${i}`}
                                                        className={`
                                ch-leaf-item cursor-pointer padding-x--xs border-radius--s transition-all
                                ${
                                                            hovered?.value === item.val
                                                                ? "bg-indigo-100 text-indigo-700 font-bold scale-110"
                                                                : ""
                                                        }
                              `}
                                                        onMouseEnter={() =>
                                                            setHovered({
                                                                stemLabel,
                                                                stemBase: item.stemBase,
                                                                leaf: item.leaf,
                                                                value: item.val,
                                                            })
                                                        }
                                                        onMouseLeave={() => setHovered(null)}
                                                        onClick={() => handleRemove(item.val)}
                                                        title="Click to remove"
                                                    >
                              {item.leaf}
                            </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* --- CONTROLS SIDEBAR --- */}
                <div className="flexColumn gap-medium" style={{ width: 250 }}>
                    {/* Dynamic Key Card */}
                    <Card size="small" className="ch-card bg-slate-50 border-all--s text-center">
            <span className="ch-text-small text-muted font-bold uppercase tracking-wide">
              Key
            </span>

                        <div className="font-mono text-xl margin-t--xs animate-fade-in">
                            <span className="text-slate-500">{keyStem}</span>
                            <span className="text-slate-300 mx-2">|</span>
                            <span className="text-indigo-600 font-bold">{keyLeaf}</span>
                            <span className="mx-2">=</span>
                            <span className="text-slate-800 font-bold">{keyValue}</span>
                        </div>

                        <div className="ch-text-tiny text-muted margin-t--xs">
                            {hovered ? "Current Selection" : "Example"}
                        </div>

                        {showBackendKey && (
                            <div className="ch-text-tiny text-slate-500 margin-t--xs">
                                {config.keyDescription}
                            </div>
                        )}
                    </Card>

                    {/* Data Input */}
                    <Card size="small" title="Edit Data" className="ch-card shadow-sm">
                        <div className="flexRow gap-small margin-b--m">
                            <Input
                                placeholder="Num (0–9999)"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onPressEnter={handleAdd}
                                type="number"
                                min={0}
                                max={9999}
                            />
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} />
                        </div>

                        <div className="flexColumn gap-small">
                            <div className="flexSpaceBetween flexAlignCentre">
                                <span className="ch-text-small">Order Leaves</span>
                                <Switch size="small" checked={isOrdered} onChange={setIsOrdered} />
                            </div>

                            <div className="flexSpaceBetween flexAlignCentre">
                <span className="ch-text-small flexAlignCentre gap-xs">
                  Split Stems
                  <Tooltip title="Splits stems into low (0-4) and high (5-9) halves. Useful for dense data.">
                    <InfoCircleOutlined className="text-muted" />
                  </Tooltip>
                </span>
                                <Switch size="small" checked={isSplit} onChange={setIsSplit} />
                            </div>
                        </div>
                    </Card>

                    <div className="text-center">
                        <Tag color="blue">{data.length} Data Points</Tag>
                    </div>
                </div>
            </div>
        </div>
    );
};
